import React from "react";
import { FormStepContext } from "./formContext";
import PricingSummary from "./PricingSummary";

interface FormStep3Props {
  register: any;
  getValues: Function;
  formStep: number;
  parsedUrlInfo: any;
  taxBreakdown: any[];
  numPmts: number;
  couponState: any;
  isRoomMode: boolean;
  isDesktop?: boolean;
  fullDate: (date: Date) => string;
}

export default function FormStep3({
  register,
  getValues,
  formStep,
  parsedUrlInfo,
  taxBreakdown,
  numPmts,
  couponState,
  isRoomMode,
  isDesktop,
  fullDate,
}: FormStep3Props) {
  return (
    <section>
      {/* 🡤 Logistics Summary */}
      <label
        id="logistics-label"
        htmlFor="logistics-summary"
        className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
      >
        Class & Guest Details
      </label>
      <div
        id="logistics-summary"
        className="bg-[#f7f0ea] border border-gray-300 rounded-lg p-4 shadow-sm mb-6"
        aria-labelledby="logistics-label"
      >
        <div className="grid grid-cols-2 gap-x-1 text-sm text-left">
          {/* Column 1 */}
          <div className="space-y-2">
            <div>
              <span className="font-semibold">{isRoomMode ? "Room" : "Class"}:</span> {getValues("ExperienceTitle")}
            </div>
            <div>
              <span className="font-semibold">Guests:</span> {parseInt(getValues("NumberofGuests"))}
            </div>
            <div>
              <span className="font-semibold">Arrival:</span> {fullDate(getValues("dates")[0])}
            </div>
          </div>
          {/* Column 2 */}
          <div className="space-y-2">
            <div>
              {["0", "1"].includes(getValues("Experience")) ? (
                <>
                  <span className="font-semibold">Length:</span> {getValues("Experience") === "0" ? "3 Days" : "5 Days"}
                </>
              ) : (
                <>
                  <span className="font-semibold">Nights:</span> {getValues("Experience")}
                </>
              )}
            </div>
            {/* 👇 Occupancy line or blank placeholder */}
            <div>
              {isRoomMode ? (
                <span className="italic text-gray-400">&nbsp;</span> // keeps spacing
              ) : (
                <>
                  <span className="font-semibold">Occupancy:</span> {getValues("Occupancy")}
                </>
              )}
            </div>

            <div>
              <span className="font-semibold">Departure:</span> {fullDate(getValues("dates")[1])}
            </div>
          </div>
        </div>
      </div>

      {/* 💰 Pricing & Payment Summary */}
      <label
        id="pricing-label"
        htmlFor="pricing-summary"
        className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
      >
        Pricing & Payment Schedule
      </label>
      <div id="pricing-summary" className="mb-6">
        <FormStepContext.Provider value={formStep}>
          <PricingSummary
            optionPrice={getValues("optionPrice")}
            taxBreakdown={taxBreakdown}
            numPmts={numPmts}
            register={register}
            getValues={getValues}
            hideBookingSummary={parsedUrlInfo.isRoomMode || (isDesktop && formStep === 3) || (!isDesktop && formStep === 4)}
            couponState={couponState}
            isRoomMode={parsedUrlInfo.isRoomMode}
            isDesktop={isDesktop}
          />
        </FormStepContext.Provider>
      </div>

      {/* ✅ Agreements */}
      <label
        id="agreements-label"
        htmlFor="agreements-box"
        className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
      >
        Agreements
      </label>

      <div
        id="agreements-box"
        className="space-y-6 mt-2 border-2 border-gray-300 rounded-lg p-4 bg-[#f7f0ea]"
        aria-labelledby="agreements-label"
      >
        <div className="flex items-start gap-3">
          <input
            id="tac"
            {...register("tac", { required: true })}
            type="checkbox"
            className="mt-1 p-3 text-green-600 rounded border-2 border-gray-300 cursor-pointer"
          />
          <label htmlFor="tac" className="text-sm">
            I have read and accept the{" "}
            <a
              className="text-blue-500 underline"
              target="_blank"
              rel="noopener noreferrer"
              href={
                isRoomMode ? "https://chateau-depierre.com/terms-conditions-rooms" : "https://chateau-depierre.com/terms-conditions-exp"
              }
            >
              Terms and Conditions
            </a>
            .
          </label>
        </div>

        <div className="flex items-start gap-3">
          <input
            id="pp"
            {...register("pp", { required: true })}
            type="checkbox"
            className="mt-1 p-3 text-green-600 rounded border-2 border-gray-300 cursor-pointer"
          />
          <label htmlFor="pp" className="text-sm">
            I have read and accept the{" "}
            <a
              className="text-blue-500 underline"
              href="https://chateau-depierre.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </label>
        </div>
      </div>
    </section>
  );
}
